import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      // Service providers land on their dedicated app — never on chair dashboard.
      let dest;
      if (u?.system_role === "service_provider") {
        dest = "/sp/dashboard";
      } else if (location.state?.from) {
        dest = location.state.from;
      } else if (u?.system_role === "platform_admin") {
        dest = "/admin/compliance/templates";
      } else {
        dest = "/dashboard";
      }
      navigate(dest);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    // Defensive observability — if a browser extension (uBlock, Brave Shields,
    // strict tracking-protection) silently blocks the navigation, the user will
    // at least see the toast + console log and can investigate. Without this
    // they get the impression "nothing happens" on click.
    // eslint-disable-next-line no-console
    console.log("[Opstrait] Redirecting to Google sign-in:", authUrl);
    toast.message("Redirecting to Google sign-in…", { duration: 4000 });
    // Set href on next tick so the toast renders synchronously first.
    setTimeout(() => { window.location.href = authUrl; }, 50);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--slate-light)" }}
      data-testid="login-page"
    >
      <header className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-6xl mx-auto px-6 py-5">
          <Link to="/">
            <Wordmark size={26} />
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div
          className="w-full max-w-md bg-white rounded-2xl border p-8 shadow-sm"
          style={{ borderColor: "var(--border)" }}
        >
          <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--navy)" }}>
            Welcome back
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--slate)" }}>
            Sign in to your committee dashboard.
          </p>

          <Button
            variant="outline"
            className="w-full h-11 font-semibold mb-5"
            onClick={startGoogle}
            data-testid="login-google-btn"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            <span className="text-[11px] uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>
              or use email
            </span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div
                className="rounded-md p-3 text-sm"
                style={{ background: "var(--alert-light)", color: "var(--alert)" }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-11 font-semibold"
              disabled={loading}
              style={{ background: "var(--blue)", color: "white" }}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : (<><LogIn size={16} className="mr-1.5" /> Sign in</>)}
            </Button>
          </form>

          <p className="text-sm mt-6 text-center" style={{ color: "var(--slate)" }}>
            New to Opstrait?{" "}
            <Link to="/signup" className="font-semibold" style={{ color: "var(--blue)" }} data-testid="login-to-signup">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" className="mr-2">
      <path fill="#FFC107" d="M43.6 20.4H42V20H24v8h11.3C33.5 32 29.1 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29 5 24 5 13 5 4 14 4 25s9 20 20 20 20-9 20-20c0-1.6-.2-3.1-.4-4.6z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29 5 24 5 16.3 5 9.7 9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5 0 9.5-1.9 12.9-5l-6-5c-1.9 1.3-4.3 2-6.9 2-5.1 0-9.4-3-11.2-7.3l-6.5 5C9.6 41 16.3 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.4H42V20H24v8h11.3c-.9 2.5-2.5 4.6-4.7 6l6 5c-.4.4 6.4-4.7 6.4-14 0-1.6-.2-3.1-.4-4.6z"/>
    </svg>
  );
}
